import React from 'react';
import '../templates.css';

/**
 * SLC Classic Template - Formal government-style layout
 */
const SLCClassic = ({ data, schoolInfo }) => {
  const d = data || {};
  const school = schoolInfo || {};
  const formatDate = (dt) => dt ? new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '';

  const Row = ({ label, value }) => (
    <tr>
      <td className="tpl-label">{label}</td>
      <td className="tpl-value">{value || '—'}</td>
    </tr>
  );

  const Row2 = ({ l1, v1, l2, v2 }) => (
    <tr>
      <td className="tpl-label">{l1}</td>
      <td className="tpl-value">{v1 || '—'}</td>
      <td className="tpl-label">{l2}</td>
      <td className="tpl-value">{v2 || '—'}</td>
    </tr>
  );

  return (
    <div className="tpl-page tpl-slc-classic">
      <div className="tpl-border">
        {/* Header */}
        <div className="tpl-header">
          {school.schoolLogo && <img src={school.schoolLogo} alt="" className="tpl-logo" />}
          <div className="tpl-header-text">
            <h1 className="tpl-school-name">{school.schoolName || 'School Name'}</h1>
            {school.schoolAddress && <p className="tpl-school-addr" dangerouslySetInnerHTML={{ __html: school.schoolAddress.replace(/<br\s*\/?>/gi, '<br/>') }} />}
            {school.schoolEmail && <p className="tpl-school-phone">{school.schoolEmail}</p>}
          </div>
        </div>

        <div className="tpl-title-bar">
          <h2>SCHOOL LEAVING CERTIFICATE</h2>
        </div>

        <div className="tpl-cert-meta">
          <span>Certificate No: <strong>{d.Certificate_Number || d.Certificate_Id || '—'}</strong></span>
          {d.Book_Number && <span>Book No: <strong>{d.Book_Number}</strong></span>}
        </div>

        {/* Body */}
        <table className="tpl-table">
          <tbody>
            <Row2 l1="Student Name" v1={d.Student_Name} l2="Admission No" v2={d.Admission_Number} />
            <Row2 l1="Father's Name" v1={d.Father_Name} l2="Mother's Name" v2={d.Mother_Name} />
            {d.Guardian_Name && <Row label="Guardian Name" value={d.Guardian_Name} />}
            <Row2 l1="Date of Birth" v1={formatDate(d.Date_Of_Birth)} l2="Gender" v2={d.Gender} />
            {d.Date_Of_Birth_Words && <Row label="Date of Birth (Words)" value={d.Date_Of_Birth_Words} />}
            <Row2 l1="Nationality" v1={d.Nationality} l2="Religion" v2={d.Religion} />
            <Row2 l1="Category" v1={d.Category} l2="Conduct" v2={d.Conduct} />
            <Row2 l1="Date of First Admission" v1={formatDate(d.Date_Of_First_Admission)} l2="Class First Admitted" v2={d.Class_First_Admitted} />
            <Row2 l1="Class Last Studied" v1={d.Class_Last_Studied} l2="Section" v2={d.Section_Last_Studied} />
            {d.Subjects_Studied && <Row label="Subjects Studied" value={d.Subjects_Studied} />}
            <Row2 l1="Qualified for Promotion" v1={d.Qualified_For_Promotion} l2="Exam Result" v2={d.Exam_Result} />
            <Row2 l1="Total Working Days" v1={d.Total_Working_Days} l2="Total Days Attended" v2={d.Total_Days_Attended} />
            <Row2 l1="Date of Leaving" v1={formatDate(d.Date_Of_Leaving)} l2="All Dues Cleared" v2={d.All_Dues_Cleared} />
            {d.Reason_For_Leaving && <Row label="Reason for Leaving" value={d.Reason_For_Leaving} />}
            {d.Extra_Curricular && <Row label="Extra-Curricular" value={d.Extra_Curricular} />}
            {d.Remarks && <Row label="Remarks" value={d.Remarks} />}
          </tbody>
        </table>

        {/* Footer */}
        <div className="tpl-footer">
          <div className="tpl-footer-date">
            Date: {formatDate(d.Certificate_Date) || formatDate(new Date())}
          </div>
          <div className="tpl-signatures">
            <div className="tpl-sig">
              <div className="tpl-sig-line" />
              <span>Class Teacher</span>
            </div>
            <div className="tpl-sig">
              <div className="tpl-sig-line" />
              <span>Principal</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * SLC Modern Template - Clean modern design with accent colors
 */
const SLCModern = ({ data, schoolInfo }) => {
  const d = data || {};
  const school = schoolInfo || {};
  const formatDate = (dt) => dt ? new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '';

  const Field = ({ label, value }) => (
    <div className="tpl-mod-field">
      <span className="tpl-mod-label">{label}</span>
      <span className="tpl-mod-value">{value || '—'}</span>
    </div>
  );

  return (
    <div className="tpl-page tpl-slc-modern">
      {/* Modern Header */}
      <div className="tpl-mod-header">
        <div className="tpl-mod-header-bar" />
        <div className="tpl-mod-header-content">
          {school.schoolLogo && <img src={school.schoolLogo} alt="" className="tpl-logo-sm" />}
          <div>
            <h1 className="tpl-mod-school">{school.schoolName || 'School Name'}</h1>
            {school.schoolAddress && <p className="tpl-mod-addr" dangerouslySetInnerHTML={{ __html: school.schoolAddress.replace(/<br\s*\/?>/gi, '<br/>') }} />}
            {school.schoolEmail && <p className="tpl-mod-addr">{school.schoolEmail}</p>}
          </div>
        </div>
        <div className="tpl-mod-title">School Leaving Certificate</div>
        <div className="tpl-mod-certno">
          No: {d.Certificate_Number || d.Certificate_Id || '—'}
        </div>
      </div>

      {/* Fields Grid */}
      <div className="tpl-mod-grid">
        <Field label="Student Name" value={d.Student_Name} />
        <Field label="Father's Name" value={d.Father_Name} />
        <Field label="Mother's Name" value={d.Mother_Name} />
        <Field label="Date of Birth" value={formatDate(d.Date_Of_Birth)} />
        <Field label="Gender" value={d.Gender} />
        <Field label="Nationality" value={d.Nationality} />
        <Field label="Religion" value={d.Religion} />
        <Field label="Category" value={d.Category} />
        <Field label="Admission No" value={d.Admission_Number} />
        <Field label="Class Last Studied" value={d.Class_Last_Studied} />
        <Field label="Section" value={d.Section_Last_Studied} />
        <Field label="Conduct" value={d.Conduct} />
        <Field label="Date of Leaving" value={formatDate(d.Date_Of_Leaving)} />
        <Field label="Reason" value={d.Reason_For_Leaving} />
        <Field label="Qualified for Promotion" value={d.Qualified_For_Promotion} />
        <Field label="All Dues Cleared" value={d.All_Dues_Cleared} />
      </div>

      {d.Remarks && (
        <div className="tpl-mod-remarks">
          <strong>Remarks:</strong> {d.Remarks}
        </div>
      )}

      {/* Footer */}
      <div className="tpl-mod-footer">
        <span>Date: {formatDate(d.Certificate_Date) || formatDate(new Date())}</span>
        <div className="tpl-mod-sigs">
          <div className="tpl-sig"><div className="tpl-sig-line" /><span>Class Teacher</span></div>
          <div className="tpl-sig"><div className="tpl-sig-line" /><span>Principal</span></div>
        </div>
      </div>
    </div>
  );
};

export { SLCClassic, SLCModern };
